import { useNavigate } from "react-router-dom";
import { useTheme } from "./ThemeContext";

export default function AboutUs() {
  const navigate = useNavigate();
  const { toggleTheme } = useTheme();

  const styles = `
    @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:wght@300;400;500;600&display=swap');
    
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    
    :root {
      --ink: #0f1117;
      --ink-soft: #4a4f5e;
      --bg: #f7f8fa;
      --surface: #ffffff;
      --border: #e4e6ed;
      --grad: linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%);
    }
    
    [data-theme="dark"] {
      --ink: #f7f8fa;
      --ink-soft: #cbd5e1;
      --bg: #0f1117;
      --surface: #1a1f3a;
      --border: #2d3748;
    }
    
    body { font-family: 'DM Sans', sans-serif; background: var(--bg); color: var(--ink); line-height: 1.8; }
    
    nav {
      position: sticky; top: 0; z-index: 100;
      background: rgba(247,248,250,.88);
      backdrop-filter: blur(14px);
      border-bottom: 1px solid var(--border);
      padding: 0 2rem;
      display: flex; align-items: center; justify-content: space-between;
      height: 64px;
    }
    [data-theme="dark"] nav { background: rgba(15,17,23,.88); }
    
    .nav-back { cursor: pointer; font-size: 1.2rem; color: var(--ink); transition: opacity .2s; }
    .nav-back:hover { opacity: .6; }
    
    .nav-theme-toggle {
      background: transparent; border: none; cursor: pointer;
      font-size: 1.2rem; color: var(--ink);
      padding: .4rem; border-radius: 6px;
      transition: background .2s;
    }
    .nav-theme-toggle:hover { background: rgba(102,126,234,.1); }
    
    .hero {
      padding: 5rem 2rem;
      background: linear-gradient(180deg, rgba(102,126,234,.08) 0%, rgba(118,75,162,.04) 100%);
      text-align: center;
      max-width: 1000px;
      margin: 0 auto;
    }
    
    h1 {
      font-family: 'DM Serif Display', serif;
      font-size: 2.8rem;
      margin-bottom: 1rem;
      background: var(--grad);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
    
    .subtitle {
      font-size: 1.3rem;
      color: var(--ink-soft);
      max-width: 700px;
      margin: 0 auto;
    }
    
    .content {
      max-width: 1000px;
      margin: 0 auto;
      padding: 3rem 2rem;
    }
    
    h2 {
      font-family: 'DM Serif Display', serif;
      font-size: 2rem;
      margin: 2.5rem 0 1.5rem;
      color: var(--ink);
    }
    
    h3 {
      font-size: 1.3rem;
      margin: 1.5rem 0 .8rem;
      color: var(--ink);
    }
    
    p {
      color: var(--ink-soft);
      margin-bottom: 1rem;
      line-height: 1.9;
    }
    
    .values-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
      gap: 2rem;
      margin: 2rem 0;
    }
    
    .value-card {
      background: var(--surface);
      border: 1px solid var(--border);
      border-radius: 14px;
      padding: 2rem;
      text-align: center;
      transition: all .2s;
    }
    
    .value-card:hover {
      transform: translateY(-4px);
      box-shadow: 0 8px 24px rgba(102,126,234,.15);
    }
    
    .value-icon {
      font-size: 2.5rem;
      margin-bottom: 1rem;
    }
    
    .value-card h3 {
      margin: 0 0 .5rem;
      font-size: 1.2rem;
    }
    
    .value-card p {
      margin: 0;
      font-size: .95rem;
    }
    
    .mission {
      background: linear-gradient(135deg, rgba(102,126,234,.08) 0%, rgba(118,75,162,.05) 100%);
      border-left: 4px solid #667eea;
      border-radius: 14px;
      padding: 2.5rem;
      margin: 2rem 0;
    }
    
    .mission h3 {
      margin-top: 0;
      color: #667eea;
    }
    
    .cta-button {
      background: var(--grad);
      color: #fff;
      padding: 1rem 2rem;
      border: none;
      border-radius: 10px;
      font-size: 1rem;
      font-weight: 600;
      cursor: pointer;
      transition: all .2s;
      display: inline-block;
      text-decoration: none;
      margin-top: 2rem;
    }
    
    .cta-button:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(102,126,234,.4);
    }
    
    footer {
      border-top: 1px solid var(--border);
      padding: 2rem;
      text-align: center;
      color: var(--ink-soft);
      margin-top: 4rem;
    }
  `;

  return (
    <>
      <style>{styles}</style>

      <nav>
        <div className="nav-back" onClick={() => navigate('/')}>← Back</div>
        <button className="nav-theme-toggle" onClick={toggleTheme}>
          {isDarkMode ? '☀️' : '🌙'}
        </button>
      </nav>

      <div className="hero">
        <h1>About flycodeinv.com</h1>
        <p className="subtitle">
          Building academic excellence through intelligent tools and comprehensive resources for students worldwide.
        </p>
      </div>

      <div className="content">
        <section className="mission">
          <h3>Our Mission</h3>
          <p>
            We believe that every student deserves access to tools and resources that help them succeed academically. 
            Our mission is to democratize academic excellence by providing free, comprehensive tutorials and intelligent 
            evaluation tools that empower students to reach their full potential.
          </p>
        </section>

        <h2>Who We Are</h2>
        <p>
          flycodeinv.com (Fly Code Innovation) is an educational technology platform dedicated to helping students master 
          the tools and skills they need for academic success. We curate the best tutorials, technology resources, and 
          AI-powered feedback tools for students across all academic levels.
        </p>
        <p>
          Our team includes educators, researchers, and technology professionals who understand the challenges students face. 
          We've built our platform based on years of experience in academic settings, creating solutions that actually help 
          students improve their work.
        </p>

        <h2>Our Values</h2>
        <div className="values-grid">
          <div className="value-card">
            <div className="value-icon">📚</div>
            <h3>Learning First</h3>
            <p>We prioritize genuine learning and skill development over quick fixes or shortcuts.</p>
          </div>
          <div className="value-card">
            <div className="value-icon">🤝</div>
            <h3>Accessibility</h3>
            <p>Quality academic resources should be available to all students, regardless of background.</p>
          </div>
          <div className="value-card">
            <div className="value-icon">🎯</div>
            <h3>Practical Impact</h3>
            <p>Every tool and tutorial we offer has been tested by real students in real academic settings.</p>
          </div>
          <div className="value-card">
            <div className="value-icon">🔬</div>
            <h3>Evidence-Based</h3>
            <p>Our recommendations are grounded in research and best practices in education.</p>
          </div>
          <div className="value-card">
            <div className="value-icon">💡</div>
            <h3>Innovation</h3>
            <p>We continuously explore new technologies and teaching methods to serve students better.</p>
          </div>
          <div className="value-card">
            <div className="value-icon">🌍</div>
            <h3>Global Community</h3>
            <p>We serve students from diverse backgrounds and academic traditions worldwide.</p>
          </div>
        </div>

        <h2>What We Offer</h2>
        <h3>📚 Comprehensive Tutorials</h3>
        <p>
          Our platform features tutorials across five major categories: AI Writing Tools, Citation Management, 
          Data Analysis, Project Management, and Assignment Evaluation. Each tutorial is designed to be practical, 
          clear, and immediately applicable.
        </p>

        <h3>🎓 Assignment Evaluator</h3>
        <p>
          Our flagship tool uses advanced AI to provide constructive, detailed feedback on student assignments. 
          It's designed to help students understand what works, what doesn't, and how to improve—all in real-time.
        </p>

        <h3>🛠️ Tool Mastery</h3>
        <p>
          We help students master the industry-standard tools that academics actually use. From citation management 
          to statistical analysis, we cover the tech stack that matters.
        </p>

        <h2>Why Choose Us?</h2>
        <p>
          Unlike generic tutorial sites, we focus specifically on academic excellence. Our platform is built by people 
          who understand the academic journey. We're not just teaching tools—we're helping students become better researchers, 
          writers, and critical thinkers.
        </p>

        <button className="cta-button" onClick={() => navigate('/tools/assignment-evaluator')}>
          Try Our Tools ✨
        </button>
      </div>

      <footer>
        <p>© 2026 flycodeinv.com · All Rights Reserved</p>
      </footer>
    </>
  );
}
